<template>
  <div>
    <v-row
      v-if="!isCustom"
      align="center"
      justify="end"
      class="mr-1"
    >
      <span>{{ lastSave }}</span>
    </v-row>
    <v-row
      align="start"
      justify="center"
    >
      <v-col
        cols="12"
        lg="6"
      >
        <template v-if="!isCustom">
          <VTextField
            v-model="workOrder.site.client.name"
            :label="$t('forms.labels.clientName')"
            disabled
          />
          <VTextField
            v-model="workOrder.site.name"
            :label="$t('forms.labels.siteName')"
            disabled
          />
        </template>
        <template v-if="isCustom">
          <VAutocomplete
            v-model="model.site.client"
            :label="$t('forms.labels.clientName')"
            :items="displayedClients"
            :loading="loading"
            :disabled="disableClientAndSiteInfo ? true : model.site.isEditable"
            cacheItems
            returnObject
            @search="debouncedClientSearch"
            @change="clientNameChanged"
          />
          <VSelect
            v-model="model.site.id"
            itemText="name"
            itemValue="id"
            :disabled="disableClientAndSiteInfo ? true : model.site.isEditable"
            :label="$t('forms.labels.siteName')"
            :items="sites"
            @change="siteNameChanged"
          />
        </template>
        <VTextField
          :value="isCustom ? model.site.address : workOrder.site.address"
          :label="$t('forms.labels.siteAddress')"
          :disabled="disableClientAndSiteInfo ? true : (isCustom ? model.site.isEditable : true)"
          @input="isCustom ? model.site.address = $event : workOrder.site.address = $event"
        />
        <VTextField
          :value="isCustom ? model.po : po"
          :label="$t('forms.labels.poNumber')"
          :disabled="isCustom ? model.site.isEditable : true"
          @input="isCustom ? model.po = $event : model.po = $event"
        />
      </v-col>
      <v-col
        cols="12"
        lg="6"
      >
        <VTextField
          :value="isCustom ? model.reportName : reportName"
          :label="$t('forms.labels.reportName')"
          @input="isCustom ? model.name = $event : name = $event"
          @blur="onEventUpdateWorkOrder"
        />
        <VSelect
          :value="isCustom ? model.type : type"
          :label="$t('forms.labels.type')"
          :loading="loading"
          :items="displayedReportTypes"
          :required="!isCustom"
          :error="hasNotType"
          :messages="hasNotType ? $t('forms.labels.reportTypeError') : ''"
          data-cy="woSummaryType"
          @input="isCustom ? model.type = $event : type = $event"
          @change="onEventUpdateWorkOrder"
        />
        <VTextField
          v-if="!isCustom"
          :value="startDate"
          :label="$t('forms.labels.dateTime')"
          disabled
        />
        <VTextField
          :value="isCustom ? model.so : so"
          :label="$t('forms.labels.soNumber')"
          :disabled="isCustom ? model.site.isEditable : true"
          @input="isCustom ? model.so = $event : so = $event"
        />
        <v-checkbox
          v-if="isCustom"
          v-model="model.billable"
          dense
          color="primary"
          :label="$t('forms.labels.billable')"
        />
        <v-row v-if="!isCustom">
          <v-col
            cols="12"
            lg="6"
          >
            <VDateTimePicker
              v-model="onLocationFrom"
              :label="$t('forms.labels.onLocationFrom')"
              :rules="{ required: false }"
              :locale="locale"
              @input="onClickOnLocation('onLocationFrom', $event)"
            />
          </v-col>
          <v-col
            cols="12"
            lg="6"
          >
            <VDateTimePicker
              v-model="onLocationTo"
              :label="$t('forms.labels.onLocationTo')"
              :rules="{ required: false }"
              :locale="locale"
              @input="onClickOnLocation('onLocationTo', $event)"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters, mapActions } from 'vuex';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import VDateTimePicker from '@/components/shared/widgets/forms/VuetifyInputs/VDateTimePicker.vue';
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect.vue';
import VAutocomplete from '@/components/shared/widgets/forms/VuetifyInputs/VAutocomplete.vue';
import { debounce } from 'lodash';
import _datePickerLocales from '@/locales/date-picker-locales';

export default {
    components: {
        VTextField,
        VDateTimePicker,
        VSelect,
        VAutocomplete,
    },
    props: {
        hasNotType: {
            type: Boolean,
            default: false,
        },
        model: {
            type: Object,
            default: () => ({}),
        },
        isCustom: {
            type: Boolean,
            default: false,
        },
        disableClientAndSiteInfo: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            name: null,
            type: null,
            onLocationFrom: null,
            onLocationTo: null,
            reportTypes: [],
            sites: [],
            clients: [],
            debouncedClientSearch: debounce(this.searchClients, 500),
        };
    },
    computed: {
        ...mapGetters([
            'lang',
            'workOrder',
            'client',
        ]),
        displayedClients() {
            return this.clients?.map(client => ({
                name: `${client.name} [${client.accountNumber}]`,
                id: client.id,
            }));
        },
        getModel() {
            return this.isCustom ? this.model.name : this.name;
        },
        format() {
            return this.lang === 'fr' ? '24hr' : 'ampm';
        },
        locale() {
            return _datePickerLocales[this.lang];
        },
        lastSave() {
            return `${this.$t('visitSummary.lastSave')}  : ${this.$options.filters.formatDate(this.workOrder.creationDate, 'L LT')}`;
        },
        startDate() {
            return this.$options.filters.formatDate(this.workOrder.startDate, 'L LT');
        },
        displayedReportTypes() {
            return this.reportTypes.map(reportType => ({
                id: reportType.id,
                name: reportType.label ? reportType.label[this.lang] : '',
            }));
        },
    },
    watch: {
        workOrder: {
            handler() {
                if (!this.isCustom) {
                    this.name = this.workOrder.summaryReport.name;
                    this.type = this.workOrder.summaryReport.type;
                    this.onLocationFrom = this.workOrder.onLocationFrom;
                    this.onLocationTo = this.workOrder.onLocationTo;
                    this.po = this.workOrder.poNumber;
                    this.so = this.workOrder.soNumber;
                    this.reportName = this.workOrder.reportName;
                }
            },
            immediate: true,
        },
    },
    created() {
        this.getReportTypes();
        this.getSites();

        if (this.isCustom) {
            if (this.client && this.client.id) {
                this.clients = [this.client];
            } else {
                this.getClients();
            }
        }
    },
    methods: {
        ...mapActions({
            reloadWorkOrder: 'getWorkOrderInfo',
        }),
        async searchClients(search) {
            this.loading = true;
            this.clients = await this.getClients(search);
            this.getSites();
            this.loading = false;
        },
        async onClickOnLocation(value, dateTime) {
            const { id } = this.workOrder;
            const payload = {
                [value]: moment(dateTime).toISOString(),
            };

            await this.$rq.updateWorkOrder(id, payload);
            await this.reloadWorkOrder(id);
        },
        async getReportTypes() {
            this.loading = true;
            const { data } = await this.$rq.getReportTypes();
            this.reportTypes = data;
            this.loading = false;
        },
        clientNameChanged() {
            this.model.site.address = '';
        },
        async siteNameChanged() {
            const currentSite = this.sites.find(site => site.id === this.model.site?.id);
            if (currentSite) {
                this.model.site.address = currentSite.address;
            }

            if (this.model.site?.id.length && !this.model.site.client.id?.length) {
                const data = await this.getClientBySite(this.model.site.id);
                this.model.site.client = {
                    id: data.client,
                    name: `${data.name} [${data.deliveryAccountNumber}]`,
                };
                this.model.site.address = data.address;
            }

            if (this.client.id) {
                this.model.site.address = this.client.address;
            }
        },
        async onEventUpdateWorkOrder() {
            if (this.isCustom) {
                return;
            }
            this.$emit('hasNotType', false);
            const { id } = this.workOrder;
            const payload = {
                name: this.name,
                type: this.type,
            };
            await this.$rq.updateSummaryReport(id, payload);
            await this.reloadWorkOrder(id);
        },
        async getSites() {
            const { data } = this.model.site.client.id?.length
                ? await this.$rq.getClientSites(this.model.site.client.id) : await this.$rq.getSites();
            this.sites = data.sites;
        },
        async getClients(search) {
            const { data } = await this.$rq.getClients({ search, perpage: 100 });
            this.clients = data.clients;
        },
        async getClientBySite(siteId) {
            const { data } = await this.$rq.getSiteInfo(siteId);
            return data;
        },
    },
};
</script>
